import { useAuthStatus } from "@/hooks/use-auth-status"
import { fetchPrizeDrawStatus, fetchPrizeGame, type PrizeDrawStatus, type PrizeGame } from "@/domain/marketing/api"
import { prizeGameKind } from "@/domain/marketing/prize-game"
import {
  markPrizeGameAutoShown,
  markPrizeGamePlayed,
  prizeGameAutoShownWithinPeriod,
  prizeGamePlayedWithinPeriod,
} from "@/domain/marketing/prize-game-period"
import { getMerchantId } from "@/lib/company-storage"
import { COUPON_QUERY_PARAM } from "@/lib/pending-coupon"
import { useEffect, useState } from "react"
import { useLocation, useSearchParams } from "react-router-dom"
import { PrizeGameChip } from "./PrizeGameChip"
import { PrizeGameModal } from "./PrizeGameModal"

const QUIET_ROUTES = ["/cart", "/p/", "/profile/auth"]

/** Delay after the menu renders before the game opens by itself. */
export const PRIZE_GAME_AUTO_OPEN_DELAY_MS = 1500

/** Query param of a tracked link whose destination is the prize game (`?jogo=1`). */
export const PRIZE_GAME_DEEP_LINK_PARAM = "jogo"

type Offer = { status: "pending" } | { status: "none" } | { status: "ready"; game: PrizeGame }

type Standing = { gameId: string; status: PrizeDrawStatus | null }

/**
 * Offers the merchant's active prize game: it opens by itself once per play
 * window, about 1.5 s after the menu renders, unless this device already saw
 * or played it or the server says the customer already played; never over the
 * cart, checkout, tracking or sign-in. A `?jogo=1` link opens it right away,
 * once, whatever this device saw before. The chip stays as the way back in.
 */
export function PrizeGameOffer() {
  const { pathname } = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const deepLinked = searchParams.get(PRIZE_GAME_DEEP_LINK_PARAM) === "1"
  const couponLanding = searchParams.get(COUPON_QUERY_PARAM) !== null
  const authenticated = useAuthStatus()
  const [offer, setOffer] = useState<Offer>({ status: "pending" })
  const [open, setOpen] = useState(false)
  const [standing, setStanding] = useState<Standing | null>(null)
  const quiet = QUIET_ROUTES.some((prefix) => pathname.startsWith(prefix))
  const pending = offer.status === "pending"
  const game = offer.status === "ready" ? offer.game : null
  const gameId = game?.id
  const status = authenticated && standing && standing.gameId === gameId ? standing.status : undefined
  const standingKnown = !authenticated || status !== undefined

  useEffect(() => {
    if (!pending || quiet || !getMerchantId()) return
    let cancelled = false
    void fetchPrizeGame().then((found) => {
      if (!cancelled) setOffer(found ? { status: "ready", game: found } : { status: "none" })
    })
    return () => {
      cancelled = true
    }
  }, [pending, quiet])

  useEffect(() => {
    if (!gameId || !authenticated) return
    let cancelled = false
    void fetchPrizeDrawStatus(gameId).then((found) => {
      if (!cancelled) setStanding({ gameId, status: found })
    })
    return () => {
      cancelled = true
    }
  }, [gameId, authenticated])

  useEffect(() => {
    const merchantId = getMerchantId()
    if (!game || quiet || open || !standingKnown || !merchantId) return
    if (deepLinked) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete(PRIZE_GAME_DEEP_LINK_PARAM)
          return next
        },
        { replace: true },
      )
      markPrizeGameAutoShown(merchantId, game.id)
      setOpen(true)
      return
    }
    if (couponLanding || (status && !status.canPlay)) return
    if (prizeGamePlayedWithinPeriod(merchantId, game) || prizeGameAutoShownWithinPeriod(merchantId, game)) return
    const timer = window.setTimeout(() => {
      markPrizeGameAutoShown(merchantId, game.id)
      setOpen(true)
    }, PRIZE_GAME_AUTO_OPEN_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [game, quiet, open, standingKnown, status, deepLinked, couponLanding, setSearchParams])

  if (!game) return null
  const playedGameId = game.id

  function handlePlayed() {
    const merchantId = getMerchantId()
    if (merchantId) markPrizeGamePlayed(merchantId, playedGameId)
  }

  function handleOpenChange(next: boolean) {
    const merchantId = getMerchantId()
    if (!next && merchantId) markPrizeGameAutoShown(merchantId, playedGameId)
    setOpen(next)
  }

  return (
    <>
      <PrizeGameModal
        game={game}
        open={open && !quiet}
        authenticated={authenticated}
        status={status}
        onOpenChange={handleOpenChange}
        onPlayed={handlePlayed}
      />
      {!open && !quiet ? <PrizeGameChip kind={prizeGameKind(game.type)} onOpen={() => setOpen(true)} /> : null}
    </>
  )
}
